import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  BenefitsEmployeesPageDataCollectionDocument,
  BulkActivateBenefitsEmployeeActivationDocument,
  BulkActivateBenefitsEmployeeActivationMutation,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type EmployeeDetailsMap } from 'modules/benefits/pages/EmployeesPage/ActivationResultSidePanel'

import { BenefitsComplianceStep } from './components/BenefitsComplianceStep'
import {
  BenefitsSpendingLimitOverrideStep,
  validateBenefitsSpendingLimitOverrideStep,
} from './components/BenefitsEditSpendingLimitOverrideStep/index'
import {
  BenefitsEmployeeSelectorStep,
  validateBenefitsEmployeeSelectorStep,
} from './components/BenefitsEmployeeSelectorStep/index'
import BenefitsSummaryStep from './components/BenefitsSummaryStep'
import { type WizardData } from './types'

export type BenefitsEmployeeActivation = NonNullable<
  BulkActivateBenefitsEmployeeActivationMutation['benefitsMutations']['bulkActivateBenefitsEmployeeActivation']['employeeActivation']
>

interface ActivateBenefitsModalProps {
  onClose: () => void
  employeeIdsWithBenefits: string[]
  onActivationResult: (
    data?: BenefitsEmployeeActivation & {
      employeeDetailsMap?: EmployeeDetailsMap
    }
  ) => void
}

const ActivateBenefitsModal = ({
  onClose,
  employeeIdsWithBenefits,
  onActivationResult,
}: ActivateBenefitsModalProps) => {
  const { company } = useCurrentSession()

  const benefitsSummaryStep: WizardStep<WizardData> = {
    title: i18n.t('benefits.tabs.employees.activation_results.modal.review_benefits.step_title'),
    nextText: i18n.t('common.next'),
    render: (props) => <BenefitsSummaryStep {...props} />,
    isCompleted: (_data) => true,
  }

  const benefitsEmployeeSelectorStep: WizardStep<WizardData> = {
    title: i18n.t('benefits.tabs.employees.activation_results.modal.select_employees.step_title'),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <BenefitsEmployeeSelectorStep {...props} employeeIdsWithBenefits={employeeIdsWithBenefits} />
    ),
    isCompleted: (data) => validateBenefitsEmployeeSelectorStep(data),
  }

  const benefitsSpendingLimitOverrideStep: WizardStep<WizardData> = {
    title: i18n.t(
      'benefits.tabs.employees.activation_results.modal.edit_spending_limit.step_title'
    ),
    nextText: i18n.t('common.next'),
    render: (props) => <BenefitsSpendingLimitOverrideStep {...props} />,
    isCompleted: (data) => validateBenefitsSpendingLimitOverrideStep(data),
  }

  const benefitsComplianceStep: WizardStep<WizardData> = {
    title: i18n.t('benefits.tabs.employees.activation_results.modal.accept_compliance.step_title'),
    nextText: i18n.t(
      'benefits.tabs.employees.activation_results.modal.accept_compliance.step_button'
    ),
    render: (props) => <BenefitsComplianceStep {...props} />,
    isCompleted: (_data) => true,
  }

  const steps = [
    benefitsSummaryStep,
    benefitsEmployeeSelectorStep,
    benefitsSpendingLimitOverrideStep,
    benefitsComplianceStep,
  ]

  const [bulkActivateBenefits] = useMutation(BulkActivateBenefitsEmployeeActivationDocument, {
    extractorFn: (data) => data.benefitsMutations.bulkActivateBenefitsEmployeeActivation,
    refetchQueries: [BenefitsEmployeesPageDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  return (
    <Wizard.Modal
      onClose={onClose}
      size="large"
      steps={steps}
      initialData={{
        employee_selection: { type: 'manual', ids: [] },
        employee_details_map: {},
        spending_limits: {},
      }}
      onSubmit={async (data) => {
        const result = await bulkActivateBenefits({
          variables: {
            employeeSelection: data.employee_selection,
          },
        })

        const handler = handleResult(result, () => undefined)

        const activationData = handler.success
          ? handler.data?.employeeActivation || undefined
          : undefined
        if (activationData && onActivationResult) {
          track(trackingEvents.benefits.userCompletesBenefitsActivationOnboarding, {
            company_id: company.id,
          })

          app.setFlash({
            type: 'correct',
            message: i18n.tp(
              'benefits.tabs.employees.activation_results.modal.compliance_success_message',
              {
                count: handler.success
                  ? handler.data?.employeeActivation?.successfulEmployees.length
                  : 0,
              }
            ),
          })

          onActivationResult({
            ...activationData,
            employeeDetailsMap: data.employee_details_map,
          })
        } else {
          onActivationResult(undefined)
        }
      }}
    />
  )
}

export default ActivateBenefitsModal
